import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { resetPassword } from "~/api/auth"
import { PageShell } from "~/components/PageShell"

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) return
    if (password !== passwordConfirmation) {
      setError("Passwords do not match")
      return
    }
    if (!password) return
    setSubmitting(true)
    try {
      await resetPassword(token, password)
      navigate("/dashboard", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password reset failed")
    } finally {
      setSubmitting(false)
    }
  }

  if (!token) {
    return (
      <PageShell innerClassName="max-w-lg w-full">
        <section className="w-full rounded-lg bg-[var(--color-surface-card)] p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            Reset password
          </h1>
          <p className="mt-4 text-sm text-red-600" role="alert">
            This reset link is invalid. Request a new one below.
          </p>
          <Link
            to="/forgot-password"
            className="mt-6 inline-block text-sm font-medium text-[var(--color-text-primary)] underline"
          >
            Request reset link
          </Link>
        </section>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full">
      <section className="w-full rounded-lg bg-[var(--color-surface-card)] p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Reset password
        </h1>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Choose a new password for your account.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <label
            htmlFor="reset-password"
            className="block text-sm font-medium text-[var(--color-text-primary)]"
          >
            New password
          </label>
          <input
            id="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)] focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
          />
          <label
            htmlFor="reset-password-confirmation"
            className="block text-sm font-medium text-[var(--color-text-primary)]"
          >
            Confirm password
          </label>
          <input
            id="reset-password-confirmation"
            type="password"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            required
            className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)] focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
          />
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-[var(--color-text-primary)] px-6 py-3 font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Reset password"}
          </button>
        </form>
      </section>
      <p className="text-center text-sm text-[var(--color-text-secondary)]">
        <Link to="/login" className="text-[var(--color-text-primary)] underline">
          Back to log in
        </Link>
      </p>
    </PageShell>
  )
}
